import { getUserDetail, UserDetail } from '@lovoldsystem/core.client'
import { DeleteOutline, DriveFileMoveOutlined } from '@mui/icons-material'
import { Box, Chip, Tooltip } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { t } from 'i18next'
import { useMemo } from 'react'
import { AuthorizationType } from 'src/enums/auth'
import { IEmptyStatusProps } from 'src/generated-types/empty-status'
import { IFormSchemaDto, IFormSchemaReport } from 'src/generated-types/form-schema'
import { ITaskFormSchema } from 'src/generated-types/taskFormSchema'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { getFormattedDate } from 'src/utils'
import NoReports from '../../assets/svg/no-reports.svg'
import { GridActionButton } from '../common/GridActionButton'
import { EmptyStatusCard } from '../mooring/EmptyStatusCard'
import { CenteredSpinner, Spinner } from '../Spinner'
import GenericTable from '../table/GenericTable'
import ReportViewer from './ReportViewer'

interface IOverallReportListContainer extends IEmptyStatusProps {
  onComplete: (row: any, reportId?: number) => void
  disabled?: boolean
  bulkUploadLoading?: number
  createPermissions: number[]
  context?: React.Context<any> | null
  handleExport: (row: any) => void
  data: IFormSchemaDto[] | undefined
  requiredReports?: ITaskFormSchema[]
  deletePermissions: number[]
  handleDelete: (id: number) => void
  isLoading: boolean
  onMove?: (row: IFormSchemaReport) => void
  height?: number | string
  rowsToFit?: { min?: number; max?: number; fill?: boolean }
  tableRowHeight?: number
  actionButtonheightAndWidth?: string
  iconWidth?: string
  autoHeight?: boolean
  maxHeight?: number | string
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
  useCustomSpinner?: boolean
  setDataLength?: (length: number) => void
  isScheduledTaskIgnored?: boolean
}

const OverallReportListContainer: React.FC<IOverallReportListContainer> = ({
  deletePermissions,
  handleDelete,
  isLoading,
  height,
  rowsToFit,
  data,
  requiredReports,
  onComplete,
  onMove,
  disabled,
  handleExport,
  bulkUploadLoading,
  context,
  createPermissions,
  emptyStatusImageWidth,
  emptyStatusContent,
  emptyStatusTitle,
  emptyStatusTitleFontSize,
  emptyStatusContentFontSize,
  emptyStatusContentSpacing,
  imageContainerClasses,
  iconWidth,
  tableRowHeight,
  actionButtonheightAndWidth,
  autoHeight = false,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
  useCustomSpinner = false,
  setDataLength,
  isScheduledTaskIgnored = false,
}) => {
  const { showDialog } = useDialogCtx()

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'name',
      headerName: t('name', { ns: 'common' }),
      flex: 1,
      renderCell: ({ row }) => row?.formSchemaVersion?.formSchema?.name,
    },
    {
      field: 'description',
      headerName: t('description', { ns: 'common' }),
      flex: 1,
      renderCell: ({ row }) => row?.formSchemaVersion?.formSchema?.description,
    },
    {
      field: 'createdBy',
      headerName: t('createdBy', { ns: 'common' }),
      flex: 1,
      renderCell: ({ row }) => (row?.createdByUser ? getUserDetail(row?.createdByUser, UserDetail.FullName) : ''),
    },
    {
      field: 'createdAt',
      headerName: t('createdAt', { ns: 'common' }),
      minWidth: 170,
      renderCell: ({ row }) =>
        row?.createdAt ? getFormattedDate(new Date(row?.createdAt + 'Z'), 'YYYY-MM-DD, HH:mm') : '',
    },
    {
      field: 'actions',
      headerName: '',
      flex: 1,
      minWidth: 120,
      renderCell: params => {
        const row = params.row as IFormSchemaReport
        const buttonSize = actionButtonheightAndWidth ?? '32px'

        const moveButton = onMove ? (
          <Tooltip title={t('move-report', { ns: 'form-schema-report' }) as string} arrow placement="bottom">
            <Box>
              <GridActionButton
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  onMove(row)
                }}
                context={context}
                permissionBinaryValues={deletePermissions}
                authorizationType={AuthorizationType.Disabled}
                disabled={disabled}
                color="primaryMain"
                buttonIcon={<DriveFileMoveOutlined sx={{ fontSize: iconWidth ?? '20px' }} />}
                height={buttonSize}
                width={buttonSize}
              />
            </Box>
          </Tooltip>
        ) : null

        if (row?.createdByUser == undefined) {
          return (
            <GridActionButton
              variant="outlined"
              sx={{ paddingX: '5px' }}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                onComplete(row?.formSchemaVersion?.formSchema)
              }}
              disabled={disabled}
              buttonText={t('complete', { ns: 'common' })}
              height={actionButtonheightAndWidth ?? '30px'}
            />
          )
        }

        if (row?.isDraft && !isScheduledTaskIgnored) {
          return (
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" gap={1} width="100%">
              <Tooltip
                title={(row?.draftCaption || t('draft', { ns: 'form-schema-report' })) as string}
                arrow
                placement="bottom"
              >
                <Chip
                  label={t('draft', { ns: 'form-schema-report' })}
                  size="small"
                  variant="outlined"
                  sx={{ cursor: 'default' }}
                />
              </Tooltip>
              {moveButton}
              <GridActionButton
                onClick={async e => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleDelete(row.id as number)
                }}
                context={context}
                permissionBinaryValues={deletePermissions}
                authorizationType={AuthorizationType.Disabled}
                disabled={disabled}
                color="primaryMain"
                buttonIcon={<DeleteOutline sx={{ fontSize: iconWidth ?? '20px' }} />}
                height={buttonSize}
                width={buttonSize}
              />
            </Box>
          )
        }

        return bulkUploadLoading == row.id ? (
          <Spinner size={40} />
        ) : (
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" gap={1} width="100%">
            {moveButton}
            <GridActionButton
              onClick={async e => {
                e.preventDefault()
                e.stopPropagation()
                handleDelete(row.id as number)
              }}
              context={context}
              permissionBinaryValues={deletePermissions}
              authorizationType={AuthorizationType.Disabled}
              disabled={disabled}
              color="primaryMain"
              buttonIcon={<DeleteOutline sx={{ fontSize: iconWidth ?? '20px' }} />}
              height={buttonSize}
              width={buttonSize}
            />
          </Box>
        )
      },
    },
  ]

  const rows = useMemo(() => {
    let returnArray: IFormSchemaReport[] = []
    data?.map((s, index, array) => {
      if (s.formSchemaReports && s.formSchemaReports.length > 0) {
        returnArray = [...returnArray, ...s.formSchemaReports]
      } else {
        return array
      }
    }) || []
    if (requiredReports) {
      const requiredArray = requiredReports
        ?.map(d => {
          const formSchema = data?.find(r => r?.id == d?.formSchemaId)
          return { id: d.id, formSchemaVersion: formSchema?.formSchemaVersion, values: '' } as IFormSchemaReport
        })
        .filter(
          ra => !returnArray?.find(r => r?.formSchemaVersion?.formSchemaId == ra?.formSchemaVersion?.formSchema?.id),
        )

      return requiredArray?.concat(returnArray)
    }
    setDataLength && setDataLength(returnArray?.length ?? 0)
    return returnArray
  }, [data])

  // Only enable autoHeight if autoHeight is true and if totalCount is less than maxVisibleRowCount (if provided)
  const enableAutoHeight = autoHeight && (rows?.length ?? 0) < (maxVisibleRowCount ?? 0)

  return useCustomSpinner && isLoading ? (
    <CenteredSpinner height={100} />
  ) : (
    <GenericTable
      rowsToFit={rowsToFit}
      id={'overall-report-table'}
      columns={STATIC_COLUMNS}
      data={rows}
      loading={isLoading}
      autoHeight={enableAutoHeight}
      getDetailPanelHeight={() => 'auto'}
      height={rows.length == 0 ? '100%' : enableAutoHeight ? 'auto' : height}
      minRowHeight="36px"
      getRowHeight={() => tableRowHeight ?? 40}
      onRowClick={({ row }) => {
        const value = row as IFormSchemaReport
        if (value?.isDraft && !isScheduledTaskIgnored) {
          onComplete(value.formSchemaVersion?.formSchema, row?.id)
          return
        }
        if (value?.createdAt) {
          showDialog(
            ReportViewer,
            {
              componentProps: {
                layouts: JSON.parse(value?.formSchemaVersion?.layout || ''),
                schema: value.formSchemaVersion?.formSchema,
                report: value,
                disabled: disabled,
                createdAt: value?.createdAt,
                createdByUser: value?.createdByUser,
              },
              opts: {
                maxWidth: 'lg',
                fullWidth: true,
              },
            },
            undefined,
            true,
          )
        }
      }}
      getRowClassName={params =>
        `${params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd'}${
          (params.row as IFormSchemaReport)?.isDraft ? ' draft-row' : ''
        }`
      }
      noDataComponent={() => (
        <Box height="100%">
          <EmptyStatusCard
            imageWidth={emptyStatusImageWidth ?? '40px'}
            image={NoReports}
            header={emptyStatusTitle}
            content={emptyStatusContent}
            headerFontSize={emptyStatusTitleFontSize}
            contentFontSize={emptyStatusContentFontSize}
            contentClasses="mt-2"
            contentTopMargin="10px"
            contentSpacing={emptyStatusContentSpacing}
            imageContainerClasses={imageContainerClasses ?? 'pb-4 pt-4'}
            height="100%"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
    />
  )
}

export default OverallReportListContainer
